import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import {
  parsePrice,
  validatePage1 as vPage1,
  validatePage2 as vPage2,
  validatePage3 as vPage3,
  validatePage4 as vPage4,
} from './formValidators';

const INITIAL_UPLOAD = {
  images: [],
  front_deeds: [],
  back_deeds: [],
  youtube_url: '',
  vr_url: '',
  heading_post: '',
  detail_post: '',
  type_post: [],
  floor: null,
  area: null,
  unit_area: null,
  use_area: null,
  use_unit_area: null,
  bedroom: null,
  bathroom: null,
  rai: '0',
  ngan: '0',
  wah: '0',
  price: null,
  rent_price: null,
  sellout_price: null,
  owner_status: null,
  first_hand: null,
  furniture_included: null,
  phone_number: '',
  line_id: '',
  excursive_option: [],
  asset_location_latitude: null,
  asset_location_longitude: null,
};

function mapApiToState(asset, config) {
  const {
    images_url:      { AssetImagesURL = [], FrontDeedsURL = [], BackDeedsURL = [] } = {},
    media:           { YoutubeURL = '', vr_url = '' } = {},
    title            = '',
    description      = '',
    type_post:       { Type = [] } = {},
    floor            = null,
    land:            { living_size = null, living_unit = null, total_size = null, total_unit = null, rai = '0', ngan = '0', wah = '0' } = {},
    rooms:           { Bathroom = null, Bedroom = null } = {},
    sell_price       = null,
    rent_price       = null,
    sellout_price    = null,
    owner_status     = null,
    firsthand        = null,
    furniture        = null,
    contact:         { Line = '', PhoneNumber = '' } = {},
    excursive_option: { Items = [] } = {},
    location:        { coordinates: { lat = null, lng = null } = {} } = {},
  } = asset;

  return {
    images:                  AssetImagesURL,
    front_deeds:             FrontDeedsURL,
    back_deeds:              BackDeedsURL,
    youtube_url:             YoutubeURL,
    vr_url,
    heading_post:            title,
    detail_post:             description,
    type_post:               Type,
    floor,
    area:                    total_size,
    unit_area:               total_unit,
    use_area:                living_size,
    use_unit_area:           living_unit ?? config.defaultUseAreaUnit,
    rai:                     String(rai ?? '0'),
    ngan:                    String(ngan ?? '0'),
    wah:                     String(wah ?? '0'),
    bedroom:                 Bedroom,
    bathroom:                Bathroom,
    price:                   sell_price,
    rent_price,
    sellout_price,
    owner_status,
    first_hand:              firsthand,
    furniture_included:      furniture,
    phone_number:            PhoneNumber ?? '',
    line_id:                 Line ?? '',
    excursive_option:        Items ?? [],
    asset_location_latitude:  lat,
    asset_location_longitude: lng,
  };
}

function buildEditPayload(upload, id, allowComment, config) {
  const {
    images, front_deeds, back_deeds,
    youtube_url, vr_url,
    heading_post, detail_post, type_post,
    floor, area, unit_area, use_area, use_unit_area,
    bedroom, bathroom,
    rai, ngan, wah,
    price, rent_price, sellout_price,
    owner_status, first_hand, furniture_included,
    phone_number, line_id, excursive_option,
    asset_location_latitude, asset_location_longitude,
  } = upload;

  const payload = {
    asset_id:            id,
    images,
    frontdeeds:          front_deeds,
    backdeeds:           back_deeds,
    youtube_url,
    vr_url,
    title:               heading_post,
    description:         detail_post,
    type_post,
    bedroom,
    bathroom,
    sell_price:          parsePrice(price)        || null,
    rent_price:          parsePrice(rent_price)   || null,
    owner_status,
    contact_phonenumber: phone_number,
    contact_line:        line_id,
    excursive_option,
    lat:                 asset_location_latitude,
    lng:                 asset_location_longitude,
    allow_comment:       allowComment,
  };

  if (config.hasRooms)     payload.floor          = floor;
  if (config.hasArea)    { payload.total_size      = area;     payload.total_unit   = unit_area;     }
  if (config.hasUseArea) { payload.living_size     = use_area; payload.living_unit  = use_unit_area; }
  if (config.hasLandArea){ payload.rai = rai;      payload.ngan = ngan; payload.wah = wah; }
  if (config.hasFirstHand)  payload.first_hand         = first_hand;
  if (config.hasFurniture)  payload.furniture_included = furniture_included;
  if (config.hasSellOut)    payload.sellout_price       = parsePrice(sellout_price) || null;

  return payload;
}

export function useEditPropertyForm(config) {
  const { id } = useParams();

  const [upload, setUpload]              = useState({ ...INITIAL_UPLOAD, use_unit_area: config.defaultUseAreaUnit });
  const [pageNumber, setPageNumber]      = useState(1);
  const [isUpdating, setIsUpdating]      = useState(false);
  const [isLoadingOldData, setIsLoading] = useState(true);
  const [detailLength, setDetailLength]  = useState(0);
  const [allowComment, setAllowComment]  = useState(true);
  const [formError, setFormError]        = useState(null); // { field, message }
  const [submitError, setSubmitError]    = useState(null);
  const [submitSuccess, setSubmitSuccess]= useState(false);

  // ── Fetch existing data ───────────────────────────────────────────────────

  useEffect(() => {
    const token = Cookies.get('jwt');
    if (!token) return;

    axios
      .get(`${process.env.REACT_APP_API}/fetchEditData/product/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const asset = res.data.asset;
        const mapped = mapApiToState(asset, config);
        setUpload((prev) => ({ ...prev, ...mapped }));
        setDetailLength(mapped.detail_post?.length ?? 0);
        if (asset.allowcomment !== undefined) setAllowComment(asset.allowcomment);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Field setters ─────────────────────────────────────────────────────────

  const field = (name) => (e) => {
    setFormError(null);
    setUpload((prev) => ({ ...prev, [name]: e.target.value }));
  };

  const setField = (name, value) => {
    setFormError(null);
    setUpload((prev) => ({ ...prev, [name]: value }));
  };

  const toggleArrayField = (name, value) => {
    setFormError(null);
    setUpload((prev) => {
      const arr = prev[name] ?? [];
      return {
        ...prev,
        [name]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value],
      };
    });
  };

  const handleDetailPost = (e) => {
    const text = e.target.value;
    const MAX = 1800;
    setFormError(null);
    if (text.length <= MAX) {
      setUpload((prev) => ({ ...prev, detail_post: text }));
      setDetailLength(text.length);
    } else {
      const trimmed = text.slice(0, MAX);
      setUpload((prev) => ({ ...prev, detail_post: trimmed }));
      e.target.value = trimmed;
      setDetailLength(MAX);
      setFormError({ field: 'detail_post', message: `ป้อนข้อความได้สูงสุด ${MAX} ตัวอักษร` });
    }
  };

  const handlePrice = (name) => (e) => {
    setFormError(null);
    const raw = e.target.value.replace(/,/g, '');
    if (raw === '' || raw === '0') { setUpload((prev) => ({ ...prev, [name]: raw })); return; }
    const num = parseFloat(raw);
    setUpload((prev) => ({ ...prev, [name]: isNaN(num) ? raw : num.toLocaleString() }));
  };

  const formatPrice = (val) => {
    const num = parseFloat(String(val ?? '').replace(/,/g, ''));
    return isNaN(num) ? '' : num.toLocaleString();
  };

  // ── Validation ────────────────────────────────────────────────────────────

  const validatePage1 = () => vPage1(upload);
  const validatePage2 = () => vPage2(upload, config);
  const validatePage3 = () => vPage3(upload, config);
  const validatePage4 = () => vPage4(upload);

  const PAGE_VALIDATORS = [null, validatePage1, validatePage2, validatePage3, validatePage4];

  // ── Navigation ────────────────────────────────────────────────────────────

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const goToPage = (target) => {
    if (target < pageNumber) { setPageNumber(target); scrollTop(); return; }
    for (let p = pageNumber; p < target; p++) {
      const result = PAGE_VALIDATORS[p]?.();
      if (!result?.ok) { setFormError(result); return; }
    }
    setFormError(null);
    setPageNumber(target);
    scrollTop();
  };

  const nextPage = () => {
    const result = PAGE_VALIDATORS[pageNumber]?.();
    if (result?.ok) {
      setFormError(null);
      setPageNumber((p) => p + 1);
      scrollTop();
    } else {
      setFormError(result);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    const result = validatePage4();
    if (!result?.ok) { setFormError(result); return; }

    const token = Cookies.get('jwt');
    if (!token) { setSubmitError('กรุณาเข้าสู่ระบบใหม่อีกครั้ง'); return; }

    setIsUpdating(true);
    setSubmitError(null);
    try {
      await axios.put(
        `${process.env.REACT_APP_API}/updatepost/update`,
        buildEditPayload(upload, id, allowComment, config),
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setSubmitSuccess(true);
      setTimeout(() => window.location.replace('/home'), 1500);
    } catch (err) {
      const errData = err?.response?.data;
      setSubmitError(errData?.message || errData?.Error || 'เกิดข้อผิดพลาด');
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    upload, setUpload,
    allowComment, setAllowComment,
    isLoadingOldData,
    isUpdating,
    detailLength,
    pageNumber, setPageNumber,
    formError,
    submitError,
    submitSuccess,
    field, setField, toggleArrayField,
    handleDetailPost, handlePrice, formatPrice,
    goToPage, nextPage,
    validatePage1, validatePage2, validatePage3, validatePage4,
    handleSubmit,
  };
}
